import { type QueryClient, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { createContext, useContext } from 'react'

import { CacheKeys } from '../lib/cache'
import { GENERIC_ERROR } from '../lib/errors'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import type { NewTeammate } from './confirmInfo'
import type { OrgStatus } from './user'

/** Which API the Intake form's own calls go to: staff Intake, or the customer's confirm page. */
export enum FillApi {
  STAFF = 'fill-application',
  CUSTOMER = 'confirm-info',
}

export const FillApiContext = createContext<FillApi>(FillApi.STAFF)

export function useFillApi(): FillApi {
  return useContext(FillApiContext)
}

export enum FillOpeningState {
  READ_ONLY = 'READ_ONLY',
  FMCSA = 'FMCSA',
  FORM = 'FORM',
  DROPZONE = 'DROPZONE',
}

export enum FillCard {
  BUSINESS = 'business',
  COVERAGE = 'coverage',
  EQUIPMENT = 'equipment',
  LOSS_RUNS = 'loss_runs',
}

export enum CheckSeverity {
  MISSING = 'missing',
  INVALID = 'invalid',
  WARNING = 'warning',
}

export enum IntakeRowStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  FAILED = 'FAILED',
  ABANDONED = 'ABANDONED',
}

export enum IntakeMode {
  CUSTOMER_UPLOAD = 'CUSTOMER_UPLOAD',
  STAFF_FORM = 'STAFF_FORM',
}

export enum IntakeKind {
  LOSS_RUN = 'LOSS_RUN',
  POLICY = 'POLICY',
  COI = 'COI',
  DRIVER_LIST = 'DRIVER_LIST',
  EQUIPMENT_LIST = 'EQUIPMENT_LIST',
  MVR = 'MVR',
  IFTA = 'IFTA',
  OTHER = 'OTHER',
}

export enum PickChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

export enum FillErrorCode {
  INTAKE_PENDING = 'INTAKE_PENDING',
  ALREADY_SENT = 'ALREADY_SENT',
  NOT_SHOPPING = 'NOT_SHOPPING',
  PICK_OWNER = 'PICK_OWNER',
  DOT_NOT_FOUND = 'DOT_NOT_FOUND',
  DOT_LOOKUP_FAILED = 'DOT_LOOKUP_FAILED',
  DOT_LOCKED = 'DOT_LOCKED',
  ROCKLAKE_COOLDOWN = 'ROCKLAKE_COOLDOWN',
  NO_CUSTOMER_USER = 'NO_CUSTOMER_USER',
  LINK_TARGET_REFUSED = 'LINK_TARGET_REFUSED',
  ATTESTATION_REQUIRED = 'ATTESTATION_REQUIRED',
  FILE_REJECTED = 'FILE_REJECTED',
  CHECKS_FAILED = 'CHECKS_FAILED',
  /** Customer link asked for before the application was sent. */
  NOT_SENT = 'NOT_SENT',
  /** Files dropped before the carrier has a DOT. */
  DOT_REQUIRED = 'DOT_REQUIRED',
  /** Another send of this application holds the claim. */
  SEND_IN_PROGRESS = 'SEND_IN_PROGRESS',
  ELD_CONNECT_FAILED = 'ELD_CONNECT_FAILED',
  ELD_NOT_CONNECTED = 'ELD_NOT_CONNECTED',
}

/** Where the customer's Broker of Record authorization stands. */
export enum BorStatus {
  /** Made, waiting for them to sign. */
  SENT = 'SENT',
  SIGNED = 'SIGNED',
}

/** The stable keys a check names its field by; the page jumps to `[data-fill-field=<key>]`. */
export enum FillFieldKey {
  DOT_NUMBER = 'dot_number',
  COMPANY_NAME = 'company_name',
  ENTITY_TYPE = 'entity_type',
  ADDRESS = 'address',
  CA_NUMBER = 'ca_number',
  CANCELLATION_REASON = 'cancellation_reason',
  OWNER_NAME = 'owner_name',
  OWNER_EMAIL = 'owner_email',
  OWNER_DOB = 'owner_dob',
  OWNER_MARITAL_STATUS = 'owner_marital_status',
  TRAVEL_DISTANCE = 'travel_distance',
  FREIGHT = 'freight',
  START_DATE = 'start_date',
  COVERAGES = 'coverages',
  DRIVERS = 'drivers',
  EQUIPMENT = 'equipment',
  /** One driver's own line, "missing license number to John Doe"; its roster stands in for it on the page. */
  DRIVER_ROWS = 'driver_rows',
  /** One truck's or trailer's own line; its roster stands in for it on the page. */
  EQUIPMENT_ROWS = 'equipment_rows',
}

export const AWAITING_CONFIRMATION_REASON = 'Waiting for the customer to confirm their information.'

export const FILL_STATE_POLL_MS = 3_000

export interface FillPerson {
  id: string
  name: string
  email: string
}

export interface FillOrgRef {
  id: string
  name: string
}

export interface IntakeDocumentRow {
  id: string
  fileName: string
  /** What the Documents tab calls it: a name someone set, else the parsed title, else the file name. */
  title?: string
  status: IntakeRowStatus
  kinds: IntakeKind[]
}

/** How the files read went: per file, tallied, and what they added. */
export interface IntakeSummary {
  documents: IntakeDocumentRow[]
  total: number
  done: number
  failed: number
  pending: number
  abandoned: number
  counts: Record<string, number>
  foundSentence: string
  missingKinds: string[]
  warnings: string[]
  failedNames: string[]
}

export interface IntakeBatchSummary extends IntakeSummary {
  batchId: string
}

/** A file still being read (from any batch, the customer's own uploads included), or one that was stopped. */
export interface IntakeRow {
  id: string
  documentId: string
  fileName: string
  title?: string
  status: IntakeRowStatus
  mode: IntakeMode
  kinds: IntakeKind[]
  /** Ran past the task's whole time budget: shown as failed, and no longer holds back sending. */
  stale: boolean
  /** Why a stopped row stopped, e.g. "Stopped: the customer sent their application." */
  error: string
}

export interface OwnerSuggestion {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  dob: string | null
  /** The server matched the names against the application's owner. */
  samePerson: boolean
}

export interface EmailRecipients {
  /** Gets "Your prices are in" and the sign-in link. */
  priceAndLink: FillPerson | null
  /** The other business owners, who also get "Your quote is ready". */
  quoteReady: FillPerson[]
}

export interface FillCheck {
  card: FillCard
  /** The confirm-check card it came from: business, owner, distance, freight, drivers, equipment, or a staff one. */
  key: string
  /** A `FillFieldKey`, or a key this page doesn't know (it then scrolls to the card). */
  field: string
  label: string
  severity: CheckSeverity
  /** How many answers a roster line stands for, e.g. "missing info to 3 more drivers"; one when absent. */
  answers?: number
}

export interface CustomerLinkAvailability {
  available: boolean
  refusedReason: string | null
  /** What to tell the rep when the link can't be made yet. */
  message: string
}

export interface PendingChannelPick {
  vendors: string[]
  channel: PickChannel
  at: string
}

export interface FillApplicationState {
  application: { id: string; created: string } | null
  orgStatus: OrgStatus
  openingState: FillOpeningState
  owners: FillPerson[]
  /** Several business owners and none linked: the Business tab asks who the application is for. */
  ownerPickNeeded: boolean
  /** The application's linked owner; Intake links the org's only one when nobody had. */
  businessOwnerId: string | null
  duplicateOf: FillOrgRef | null
  staffFormStartedAt: string | null
  sent: boolean
  customerConfirmedAt: string | null
  pendingChannelPick: PendingChannelPick | null
  batch: IntakeBatchSummary | null
  /** Every file on the application, from any drop; warnings staff dismissed are left out. */
  filesSummary: IntakeSummary | null
  inFlight: IntakeRow[]
  stopped: IntakeRow[]
  /** Every file on the application, newest first, whatever drop it came in. */
  files: IntakeRow[]
  ownerSuggestions: OwnerSuggestion[]
  emailRecipients: EmailRecipients
  checks: FillCheck[]
  link: CustomerLinkAvailability
  changeDotLocked: boolean
  /** When a save or a file landed after the last send; null when the carriers have everything. */
  editedSinceSent: string | null
  /** Whether this application gets "Your prices are in" emails (website applications only). */
  priceEmails: boolean
  /** The checkout and bind block is on: sent, and the customer hasn't confirmed their information. */
  needsReviewAttestation: boolean
  /** The customer's ELD as linked for quoting. */
  eld: { connected: boolean; providerName: string }
  /** The BOR a send leaves waiting; null before there is one. */
  bor: { status: BorStatus | null }
}

/** What one pull from the linked ELD brought in; all zero while the provider is still syncing. */
export interface EldImportResult {
  providerName: string
  trucks: number
  trailers: number
  drivers: number
}

export interface DotLookupResult {
  dotNumber: string
  name: string
  city: string
  state: string
  powerUnits: number | null
  duplicateOf: FillOrgRef | null
}

export interface IntakeUploadResult {
  batchId: string
  summary: IntakeBatchSummary
}

export interface NewLossRun {
  insurer: string
  dateStart: string
  dateEnd: string
}

export interface NewClaimInput {
  lossRunRequestId?: string
  newLossRun?: NewLossRun
  coverageType: string
  incidentDate: string | null
  claimCount: number | null
  paidLoss: string | null
  reserve: string | null
}

export interface CustomerLink {
  url: string
  expiresAt: string
  user: { name: string; email: string }
}

export interface FmcsaCargoHint {
  name: string
  generic: boolean
}

export interface FreightSuggestionGroup {
  label: string
  names: string[]
  hints?: { name: string; hint: string }[]
}

export interface FreightSuggestions {
  suggestions: string[]
  suggestionGroups?: FreightSuggestionGroup[]
  fmcsaCargo: FmcsaCargoHint[]
}

export interface FillApplicationErrorBody {
  code: FillErrorCode
  detail: string
  files?: string[]
  owners?: FillPerson[]
  reason?: string
  checks?: FillCheck[]
}

export function fillApplicationUrl(orgId: string, path: string, api: FillApi = FillApi.STAFF): string {
  return `/api/v1/insure/organisations/${orgId}/${api}/${path}`
}

export function getFillApplicationState(orgId: string): Promise<FillApplicationState> {
  return axios
    .get(fillApplicationUrl(orgId, 'state/'))
    .then((response) => toCamelCase(response.data) as FillApplicationState)
}

/** The page's own state. It is the only thing the page polls, every 3 s while `poll` is set. */
export function useFillApplicationState(
  orgId: string | undefined,
  { poll = false }: { poll?: boolean } = {}
) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CacheKeys.FILL_APPLICATION_STATE, orgId],
    queryFn: () => getFillApplicationState(orgId!),
    enabled: !!orgId,
    refetchInterval: poll ? FILL_STATE_POLL_MS : false,
  })
  return { state: data, isLoading, isError, refetch }
}

export function pickOwner(orgId: string, userId: string): Promise<void> {
  return axios.post(fillApplicationUrl(orgId, 'owner/'), { user_id: userId }).then(() => undefined)
}

export function promoteOwner(orgId: string, suggestionId: string): Promise<void> {
  return axios
    .post(fillApplicationUrl(orgId, 'owner/promote/'), { suggestion_id: suggestionId })
    .then(() => undefined)
}

export function lookupDot(orgId: string, dotNumber: string, api?: FillApi): Promise<DotLookupResult> {
  return axios
    .post(fillApplicationUrl(orgId, 'dot/lookup/', api), { dot_number: dotNumber })
    .then((response) => toCamelCase(response.data) as DotLookupResult)
}

export function saveDot(orgId: string, dotNumber: string, api?: FillApi): Promise<void> {
  return axios.post(fillApplicationUrl(orgId, 'dot/', api), { dot_number: dotNumber }).then(() => undefined)
}

export function uploadIntake(orgId: string, files: File[]): Promise<IntakeUploadResult> {
  const form = new FormData()
  files.forEach((file) => form.append('file', file))
  return axios
    .post(fillApplicationUrl(orgId, 'intake/'), form)
    .then((response) => toCamelCase(response.data) as IntakeUploadResult)
}

export function stopIntake(orgId: string): Promise<void> {
  return axios.post(fillApplicationUrl(orgId, 'intake/stop/')).then(() => undefined)
}

/** Hides the warnings of every file read so far; files read later show their own. */
export function dismissFileWarnings(orgId: string): Promise<FillApplicationState> {
  return axios
    .post(fillApplicationUrl(orgId, 'intake/dismiss-warnings/'))
    .then((response) => toCamelCase(response.data) as FillApplicationState)
}

/** "Skip, fill it in by hand" and "Start filling in now": stamps staff activity, creating the application if needed. */
export function startForm(orgId: string): Promise<void> {
  return axios.post(fillApplicationUrl(orgId, 'start/')).then(() => undefined)
}

export function addLossRunClaim(orgId: string, input: NewClaimInput, api?: FillApi): Promise<void> {
  return axios
    .post(fillApplicationUrl(orgId, 'loss-runs/claims/', api), toSnakeCase(input))
    .then(() => undefined)
}

export function submitApplication(orgId: string): Promise<FillApplicationState> {
  return axios
    .post(fillApplicationUrl(orgId, 'submit/'))
    .then((response) => toCamelCase(response.data) as FillApplicationState)
}

export function createCustomerLink(orgId: string): Promise<CustomerLink> {
  return axios
    .post(fillApplicationUrl(orgId, 'customer-link/'))
    .then((response) => toCamelCase(response.data) as CustomerLink)
}

/** Who a confirm link goes to: one of the customer's users, or someone new added by name and email. */
export type ConfirmLinkTarget = { userId: string } | { firstName: string; lastName: string; email: string }

/** The customer's users a confirm link can go to: their admins and business owners. */
/** The members a confirm link can go to, and who sent a large-fleet packet when they aren't one yet. */
export function useConfirmLinkRecipients(orgId: string, { enabled }: { enabled: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.FILL_APPLICATION_STATE, orgId, 'confirm-link-recipients'],
    queryFn: () =>
      axios
        .get(fillApplicationUrl(orgId, 'confirm-link/recipients/'))
        .then(
          (response) =>
            toCamelCase(response.data) as { recipients: FillPerson[]; suggested: NewTeammate | null }
        ),
    enabled,
  })
  return { recipients: data?.recipients ?? [], suggested: data?.suggested ?? null, isLoading }
}

/** A one-click sign-in for them that lands on the confirm page, for staff to copy. */
export function createConfirmLink(orgId: string, target: ConfirmLinkTarget): Promise<CustomerLink> {
  return axios
    .post(fillApplicationUrl(orgId, 'confirm-link/'), toSnakeCase(target))
    .then((response) => toCamelCase(response.data) as CustomerLink)
}

/** Emails them that link as staff wrote it; `{link}` in the message is where it goes. */
export function emailConfirmLink(
  orgId: string,
  target: ConfirmLinkTarget,
  email: { subject: string; message: string }
): Promise<void> {
  return axios
    .post(fillApplicationUrl(orgId, 'confirm-link/email/'), toSnakeCase({ ...target, ...email }))
    .then(() => undefined)
}

/** Links the ELD from Terminal's sign-in to this customer, then brings its fleet in. */
export function connectEld(orgId: string, publicToken: string, api?: FillApi): Promise<EldImportResult> {
  return axios
    .post(fillApplicationUrl(orgId, 'eld/connect/', api), { public_token: publicToken })
    .then((response) => toCamelCase(response.data) as EldImportResult)
}

export function importEldFleet(orgId: string, api?: FillApi): Promise<EldImportResult> {
  return axios
    .post(fillApplicationUrl(orgId, 'eld/import/', api))
    .then((response) => toCamelCase(response.data) as EldImportResult)
}

export function getFreightSuggestions(orgId: string, api?: FillApi): Promise<FreightSuggestions> {
  return axios
    .get(fillApplicationUrl(orgId, 'freight-suggestions/', api))
    .then((response) => toCamelCase(response.data) as FreightSuggestions)
}

export function useFreightSuggestions(orgId: string | undefined) {
  const api = useFillApi()
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.FREIGHT_SUGGESTIONS, orgId, api],
    queryFn: () => getFreightSuggestions(orgId!, api),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  })
  return {
    suggestions: data?.suggestions ?? [],
    suggestionGroups: data?.suggestionGroups,
    fmcsaCargo: data?.fmcsaCargo ?? [],
    isLoading,
  }
}

/** The `{code, detail, ...}` body these endpoints refuse with, or null. */
export function fillApplicationError(error: unknown): FillApplicationErrorBody | null {
  const data = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data
  if (!data || typeof data !== 'object') return null
  const { code, detail } = data as { code?: unknown; detail?: unknown }
  if (typeof code !== 'string' || typeof detail !== 'string') return null
  return toCamelCase(data) as FillApplicationErrorBody
}

function firstMessage(value: unknown): string | null {
  if (typeof value === 'string') return value || null
  const items = Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : []
  for (const item of items) {
    const message = firstMessage(item)
    if (message) return message
  }
  return null
}

/**
 * What to tell the rep about a failed call: a refusal's own message, else the server's `detail`
 * (rate limits come back as a plain 403 with one) or its first field error, else `fallback`.
 */
export function fillApplicationErrorMessage(error: unknown, fallback: string = GENERIC_ERROR): string {
  const refusal = fillApplicationError(error)
  if (refusal) return refusal.detail
  const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response
  if (!response || (response.status ?? 0) >= 500) return fallback
  if (!response.data || typeof response.data !== 'object') return fallback
  return (
    firstMessage((response.data as { detail?: unknown }).detail) ?? firstMessage(response.data) ?? fallback
  )
}

/** A file is still being read until it finishes, or runs past its time budget. */
export function hasFilesReading(state: Pick<FillApplicationState, 'inFlight'> | undefined): boolean {
  return !!state?.inFlight.some((row) => !row.stale)
}

/** Missing and invalid items hold back sending; warnings never do. */
export function blockingChecks(checks: FillCheck[]): FillCheck[] {
  return checks.filter((check) => check.severity !== CheckSeverity.WARNING)
}

/** One roster row's own line, which a card whose rows show what each lacks can leave out. */
export function isRowCheck(check: FillCheck): boolean {
  return check.field === FillFieldKey.DRIVER_ROWS || check.field === FillFieldKey.EQUIPMENT_ROWS
}

/** "Add date of birth" for a missing item; invalid items and warnings read as the server words them. */
export function checkLine(check: FillCheck): string {
  return check.severity === CheckSeverity.MISSING ? `Add ${check.label}` : check.label
}

/** The checks a page shows, so the field each one names can show it too; none outside a provider. */
export const FillChecksContext = createContext<FillCheck[]>([])

/** What a blocking check says about this field, for under it. */
export function useFieldCheck(field: FillFieldKey): string | undefined {
  const check = blockingChecks(useContext(FillChecksContext)).find((item) => item.field === field)
  return check && checkLine(check)
}

export function checksForCard(checks: FillCheck[], card: FillCard): FillCheck[] {
  return checks.filter((check) => check.card === card)
}

const CARD_SOURCE_KEYS = [
  CacheKeys.ORGANISATIONS,
  CacheKeys.DRIVERS,
  CacheKeys.TRUCKS,
  CacheKeys.TRAILERS,
  CacheKeys.LOSS_RUN_REQUESTS,
] as const

/** Refetches what the cards read (and, by default, the page state) after a save or a finished file. */
export async function invalidateFillApplication(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  orgId: string,
  { state = true }: { state?: boolean } = {}
): Promise<void> {
  const keys = state ? [CacheKeys.FILL_APPLICATION_STATE, ...CARD_SOURCE_KEYS] : [...CARD_SOURCE_KEYS]
  await Promise.all(keys.map((key) => queryClient.invalidateQueries({ queryKey: [key, orgId] })))
}
